/**
 * A record of which things this browser has already been shown the inside of.
 *
 * Two features keep one: enquiries, whose list flags the ones nobody has
 * opened, and clients, whose list does the same and flags a pre-consultation
 * form sitting there unread besides. Both wanted the identical machinery — a
 * `localStorage` key, a bounded list of ids, and a snapshot stable enough for
 * `useSyncExternalStore` to read during render — so it is written once here
 * and each feature names its own key over it.
 *
 * This is the browser's own bookkeeping, not the record's. Nothing on the wire
 * says whether a practitioner has read something: the backend holds states,
 * which are a different thing — an enquiry can sit at "Recently Added" having
 * been opened and thought about twice, and a client worked on by the person at
 * the next desk has still never been opened here. So read-ness is kept where
 * the reading happened, and inventing a field for it on the response is not
 * any feature's to do.
 *
 * `localStorage`, so it survives a reload rather than putting every flag back
 * on a refresh. Ids are `public_id`s and unique per record, so a browser two
 * accounts share cannot mark one another's records read — the other
 * workspace's ids simply are not in the set.
 *
 * The store is shaped like the override store in `enquiriesService`: a module
 * singleton with a stable snapshot, so every screen showing a flag or a count
 * moves together.
 */

export interface OpenedRecordsStore {
  /** Subscribes to every change made here, and returns the unsubscribe. */
  subscribe: (listener: () => void) => () => void;
  /**
   * The ids seen on this device, as a set to test rows against. Read from
   * storage on the first call rather than in an effect, so a reloaded list
   * draws its flags in the first paint instead of flashing them on and off.
   */
  get: () => ReadonlySet<string>;
  /**
   * Marks one record as seen, which is what takes its flag away and its one
   * off the rail's count.
   *
   * A no-op for an id already held — including the reference, which the
   * snapshot contract depends on — so a detail screen may call it on every
   * render without waking a single listener after the first.
   */
  mark: (id: string) => void;
  /**
   * Forgets everything, in memory and in storage.
   *
   * Test-only, and for the same reason `resetEnquiriesStore` exists: the store
   * is a module singleton over a `localStorage` key, so without this a record
   * one test opens stays opened for the next one.
   */
  reset: () => void;
}

/**
 * How many ids are kept by default. Old enough to be past the end of any list
 * anybody pages through, and small enough that the entry stays a few
 * kilobytes — the oldest fall off the front, and a record that far back
 * showing a flag again is a fair price for a key that cannot grow without
 * bound.
 */
const MAX_REMEMBERED = 500;

export const createOpenedRecordsStore = (
  storageKey: string,
  maxRemembered: number = MAX_REMEMBERED
): OpenedRecordsStore => {
  /** Oldest first, and what is written back to storage. */
  let order: string[] = [];

  /**
   * The snapshot `useSyncExternalStore` reads. Built from `order` and replaced
   * only when something below writes — a fresh `Set` per call would be an
   * infinite re-render. `undefined` until storage has been read once.
   */
  let opened: ReadonlySet<string> | undefined;

  const listeners = new Set<() => void>();

  const publish = () => listeners.forEach((listener) => listener());

  /**
   * Storage can throw outright — Safari's private mode, or a browser with site
   * data blocked. An unreadable set is an empty one: every record reads as
   * new, which is a far better failure than the list not rendering.
   */
  const readStored = (): string[] | null => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((id): id is string => typeof id === "string")
        : [];
    } catch {
      return null;
    }
  };

  const write = () => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(order));
    } catch {
      /* not fatal — the flag goes back on next load; see `readStored` */
    }
  };

  const ensureLoaded = (): ReadonlySet<string> => {
    if (!opened) {
      order = readStored() ?? [];
      opened = new Set(order);
    }
    return opened;
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    get: ensureLoaded,

    mark: (id) => {
      if (!id) return;

      const held = ensureLoaded();
      if (held.has(id)) return;

      order = [...order, id].slice(-maxRemembered);
      opened = new Set(order);
      write();
      publish();
    },

    reset: () => {
      order = [];
      opened = new Set<string>();
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        /* nothing to undo */
      }
      publish();
    },
  };
};

export default createOpenedRecordsStore;
